import { useState } from 'react';

interface Props {
  src?: string;
  name?: string;
  email?: string;
  size?: number;
}

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#84cc16'];

function deterministicColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length] ?? PALETTE[0]!;
}

function initialsFor(name?: string, email?: string): string {
  const source = (name ?? '').trim() || (email ?? '').split('@')[0] || '';
  if (!source) return '?';
  const parts = source.split(/[\s.\-_]+/).filter(Boolean);
  if (parts.length === 0) return source[0]?.toUpperCase() ?? '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

/**
 * Round avatar. Renders the IMS-provided picture when one is available,
 * otherwise falls back to a colored initials circle keyed off the email
 * (so the same user gets the same color across renders).
 */
export function Avatar({ src, name, email, size = 32 }: Props): JSX.Element {
  const [errored, setErrored] = useState(false);
  const useImage = src && !errored;
  const seed = email ?? name ?? '';
  const bg = deterministicColor(seed || '?');
  const dim = `${size}px`;

  return (
    <span
      className="avatar"
      style={{
        width: dim,
        height: dim,
        background: useImage ? 'transparent' : bg,
        fontSize: `${Math.round(size * 0.42)}px`,
      }}
      title={name ?? email ?? undefined}
      aria-label={name ?? email ?? 'User avatar'}
    >
      {useImage ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          onError={() => setErrored(true)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <span aria-hidden="true">{initialsFor(name, email)}</span>
      )}
    </span>
  );
}
